/** @jsx jsx */
/* eslint-disable max-len, no-nested-ternary, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions, react/no-array-index-key */
import React, { useMemo, useRef, useState } from 'react';
import { css, jsx } from '@emotion/core';
import {
  BoardData, DayHeader, ShiftItem, SimulationShift, StaffInfo,
} from 'domain/attend/shiftBoard/types';
import {
  defaultTimeFormatter, DOW_LABELS, effectiveWorkMinutes, hmToMinutes, isWorkCalculated, minutesToHm, parseDateStr, TimeFormatter,
} from 'domain/attend/shiftBoard/time';
import { TimeAxis, buildAxis, minutesToPercent } from 'domain/attend/shiftBoard/layout';
import { clampSpan } from 'domain/attend/shiftBoard/validation';
import { CellState } from 'components/pages/master/attend/ShiftBoardPage/useShiftBoard';
import { photoPathByStaffCode } from 'hooks/usePhotoPath';
import { PatternColor } from './PatternPalette';
import { HopeLabel } from './BoardGrid';
import StaffAvatar from './StaffAvatar';
import { LABOR_COST_TIP, mock, TRANSPORTATION_TIP } from './styles';

// 列幅は半月の表（BoardGrid）とそろえる（9/30）。合計は右に 1 列（時間 ／ 人件費 ／ 交通費の 3 段）
const NAME_W = 128;
const SUM_W = 96;
// 1 時間の最小幅。既定の 24 時間表示で 128 + 22 × 24 + 96 = 752px。iPad（横）で左のメニューを出したままでも収まる（表の幅は約 780px）。
// 以前は 46px で合計 3 列（約 1,470px）あり、iPad では必ず横スクロールになっていた（9/30）
const HOUR_MIN = 22;
// 横にスクロールしたときも名前が見えるよう、名前の列は左に貼り付ける（9/30）
const STICKY_NAME = { position: 'sticky' as const, left: 0, zIndex: 4 };
const NOT_YET = '未計算';

const styles = {
  card: css({
    // ヘッダー・本体・フッターで列幅を揃えるため、表の中は border-box に統一する
    '& *': { boxSizing: 'border-box' },
    flex: 1,
    minWidth: 0,
    background: '#fff',
    // 9/25 に内側の余白（12px）をやめて罫線で囲み、そのぶん表を広げた。上端は見出しの行の罫線を使う
    borderLeft: `1px solid ${mock.primaryM80}`,
    borderRight: `1px solid ${mock.primaryM80}`,
    borderBottom: `1px solid ${mock.primaryM80}`,
  }),
  head: css({ display: 'flex' }),
  headCell: css({
    background: mock.primaryM95, borderTop: `1px solid ${mock.primaryM80}`, borderBottom: `1px solid ${mock.primaryM80}`, fontSize: '11px', fontWeight: 700,
  }),
  hour: css({
    flex: '1 1 0', minWidth: 0, background: mock.primaryM95, borderTop: `1px solid ${mock.primaryM80}`, borderBottom: `1px solid ${mock.primaryM80}`, borderLeft: `1px solid ${mock.gray10}`, fontSize: '10px', color: mock.textSub, padding: '6px 0', textAlign: 'center', overflow: 'hidden',
  }),
  row: css({ display: 'flex', borderBottom: `1px solid ${mock.gray10}` }),
  nameCell: css({
    width: `${NAME_W}px`, flex: 'none', padding: '6px 8px', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', ...STICKY_NAME,
  }),
  avatar: css({
    width: '22px', height: '22px', borderRadius: '9999px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 700, flex: 'none',
  }),
  lane: css({
    flex: 1,
position: 'relative',
height: '46px',
cursor: 'pointer',
touchAction: 'pan-x pan-y',
    '&:hover': { boxShadow: `inset 0 0 0 2px ${mock.primaryM80}` },
  }),
  // 上の 12px は希望の帯に使う（9/24）。バーはその下に置く
  bar: css({
    position: 'absolute', top: '14px', height: '26px', borderRadius: '3px', fontSize: '11px', fontWeight: 700, display: 'block', lineHeight: '24px', padding: '0 10px', textAlign: 'center', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', touchAction: 'none', userSelect: 'none', boxSizing: 'border-box',
  }),
  wishStrip: css({
    position: 'absolute', top: '2px', height: '10px', borderRadius: '2px', fontSize: '9px', fontWeight: 700, lineHeight: '10px', padding: '0 4px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', boxSizing: 'border-box', pointerEvents: 'none',
  }),
  handle: css({
    position: 'absolute', top: 0, bottom: 0, width: '10px', cursor: 'ew-resize',
  }),
  // 合計（1 列 3 段）。1 段目: 時間、2・3 段目: 人件費・交通費（左に項目名、金額は右）
  total: css({
    width: `${SUM_W}px`, flex: 'none', borderLeft: `1px solid ${mock.primaryM80}`, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '1px', padding: '0 6px', background: mock.primaryM95, fontVariantNumeric: 'tabular-nums', lineHeight: 1.25,
  }),
  totalLine: css({
    display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '4px', whiteSpace: 'nowrap',
  }),
  foot: css({ display: 'flex', background: mock.primaryM95, borderTop: `1px solid ${mock.primaryM80}` }),
};

export type DayTimelineProps = {
  board: BoardData;
  day: DayHeader;
  editable: boolean;
  isSimulating: boolean;
  getCell: (staffCode: string, date: string) => CellState;
  awayShifts: (staffCode: string, date: string) => Array<ShiftItem>;
  hopeOf: (staffCode: string, date: string) => HopeLabel;
  colorOf: (patternId: string | null) => PatternColor;
  onBarTap: (staff: StaffInfo, index: number) => void;
  onLaneTap: (staff: StaffInfo, minutes: number) => void;
  onStaffTap: (staff: StaffInfo) => void;
  onMove: (staff: StaffInfo, index: number, shift: SimulationShift, start: string, end: string, isEndNextDay: boolean, moveType: 'MOVE' | 'RESIZE_START' | 'RESIZE_END') => void;
  showLaborCost: boolean;
  /** 保存に失敗したセルの理由（無ければ null）。名前の横に「!」を出す（9/30） */
  failedOf?: (staffCode: string, date: string) => string | null;
  /** 時間量の整形。60 進/10 進とその丸めは労務規約項目マスタに合わせる（9/23） */
  formatTime?: TimeFormatter;
  /** 刻み（分）。自分の表示設定（9/23 に人単位へ移した）。無ければ店舗の値 */
  slotMinutes?: number;
};

type DragInfo = {
  staffCode: string; index: number; mode: 'MOVE' | 'RESIZE_START' | 'RESIZE_END'; originX: number;
  startMin: number; endMin: number; curStart: number; curEnd: number; laneWidth: number; moved: boolean;
};

const yen = (n: number | null | undefined): string => (n === null || n === undefined ? '—' : `¥${Math.round(n).toLocaleString('ja-JP')}`);
// 勤務時間は計算済みならサーバの値、未計算（確定前）なら開始・終了・休憩からの目安（9/25）
const workOf = (s: SimulationShift): number => effectiveWorkMinutes(s);
const uncalculated = (s: SimulationShift): boolean => !s.deleted && s.attendType === 0 && !isWorkCalculated(s);

/**
 * モック準拠の日ビュー（横 = 時間軸）。バーの移動・伸縮は Pointer Events
 */
const DayTimeline: React.FC<DayTimelineProps> = ({
  board, day, editable, isSimulating, getCell, awayShifts, hopeOf, colorOf, onBarTap, onLaneTap, onStaffTap, onMove, showLaborCost,
  formatTime = defaultTimeFormatter, slotMinutes, failedOf,
}) => {
  const hours = formatTime;
  const axis: TimeAxis = buildAxis(board.org.dispStartHour, board.org.dispHour, slotMinutes || board.setting.slotMinutes);
  const [drag, setDrag] = useState<DragInfo | null>(null);
  const laneRef = useRef<{ [staffCode: string]: HTMLDivElement | null }>({});
  const squelch = useRef(false);
  // 顔写真の URL はキャッシュを効かせるため、スタッフ一覧が変わったときだけ作り直す
  const photoUrls = useMemo(() => {
    const m: { [code: string]: string } = {};
    board.staffs.forEach((s) => {
      m[s.staffCode] = photoPathByStaffCode(s.staffCode, s.nameplate || undefined);
    });
    return m;
  }, [board.staffs]);
  const photoOf = (s: StaffInfo) => photoUrls[s.staffCode] || '';

  const byStaff: { [c: string]: BoardData['summary']['byStaff'][0] } = {};
  board.summary.byStaff.forEach((s) => {
    byStaff[s.staffCode] = s;
  });
  const dayTotal = board.summary.byDate.filter((d) => d.date === day.date)[0];
  // その日に未計算のシフトがあれば、日の人件費・交通費も「未計算」と出す（9/25）
  const dayUncalc = board.staffs.some((st) => getCell(st.staffCode, day.date).shifts.some(uncalculated));
  const vioStaff: { [c: string]: boolean } = {};
  board.violations.forEach((v) => {
    if (v.dates.indexOf(day.date) !== -1) {
      vioStaff[v.staffCode] = true;
    }
  });

  const spanOf = (start: string, end: string, isEndNextDay: boolean): [number, number] => {
    let st = hmToMinutes(start);
    let ed = hmToMinutes(end, isEndNextDay);
    if (ed <= st) {
      ed += 1440;
    }
    if (st < axis.startMinutes) {
      st += 1440;
      ed += 1440;
    }
    return [st, ed];
  };
  const geo = (st: number, ed: number) => ({ left: minutesToPercent(axis, st), width: Math.max(0.5, minutesToPercent(axis, ed) - minutesToPercent(axis, st)) });

  const beginDrag = (staff: StaffInfo, index: number, s: SimulationShift, e: React.PointerEvent) => {
    if (!editable || day.locked || !s.start || !s.end) {
      return;
    }
    const lane = laneRef.current[staff.staffCode];
    if (!lane) {
      return;
    }
    e.stopPropagation();
    e.preventDefault();
    const bar = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const off = e.clientX - bar.left;
    const mode: DragInfo['mode'] = off < 12 ? 'RESIZE_START' : off > bar.width - 12 ? 'RESIZE_END' : 'MOVE';
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    const [st, ed] = spanOf(s.start, s.end, s.isEndNextDay);
    setDrag({
      staffCode: staff.staffCode, index, mode, originX: e.clientX, startMin: st, endMin: ed, curStart: st, curEnd: ed, laneWidth: lane.clientWidth, moved: false,
    });
  };
  const onDragMove = (e: React.PointerEvent) => {
    if (!drag) {
      return;
    }
    const slot = axis.slotMinutes;
    const delta = Math.round((((e.clientX - drag.originX) / drag.laneWidth) * axis.totalMinutes) / slot) * slot;
    let s = drag.startMin;
    let en = drag.endMin;
    if (drag.mode === 'MOVE') {
      s += delta;
      en += delta;
    } else if (drag.mode === 'RESIZE_START') {
      s += delta;
    } else {
      en += delta;
    }
    [s, en] = clampSpan(s, en, axis.startMinutes, axis.totalMinutes, slot);
    if (drag.mode === 'MOVE') {
      const len = drag.endMin - drag.startMin;
      if (en - s !== len) {
        if (s === axis.startMinutes) {
          en = s + len;
        } else {
          s = en - len;
        }
      }
    }
    setDrag({
      ...drag, curStart: s, curEnd: en, moved: drag.moved || s !== drag.startMin || en !== drag.endMin,
    });
  };
  const onDragEnd = (staff: StaffInfo, index: number, s: SimulationShift) => {
    if (!drag) {
      return;
    }
    const d = drag;
    setDrag(null);
    if (!d.moved) {
      onBarTap(staff, index);
      return;
    }
    squelch.current = true;
    window.setTimeout(() => { squelch.current = false; }, 250);
    onMove(staff, index, s, minutesToHm(d.curStart), minutesToHm(d.curEnd), Math.floor(d.curEnd / 1440) > Math.floor(d.curStart / 1440) || (d.curEnd >= 1440 && d.curStart < 1440), d.mode);
  };

  const hourLabels: Array<string> = [];
  for (let m = axis.startMinutes; m < axis.startMinutes + axis.totalMinutes; m += 60) {
    hourLabels.push(String(Math.floor(m / 60) % 24));
  }
  const dd = parseDateStr(day.date);
  let totalCount = 0;
  let totalMinutes = 0;

  return (
    <div css={styles.card}>
      <div style={{ overflowX: 'auto' }}>
        <div style={{ minWidth: `${NAME_W + hourLabels.length * HOUR_MIN + SUM_W}px` }}>
          <div css={styles.head}>
            <div
              css={styles.headCell}
              style={{
 width: `${NAME_W}px`, flex: 'none', padding: '6px 8px', ...STICKY_NAME,
}}
            >
              {`${dd.getMonth() + 1}/${dd.getDate()}（${DOW_LABELS[dd.getDay()]}）${day.confirmed ? ' 確定' : ''}`}
            </div>
            {hourLabels.map((h, i) => <div key={i} css={styles.hour}>{h}</div>)}
            <div
              css={styles.headCell}
              style={{
 width: `${SUM_W}px`, flex: 'none', borderLeft: `1px solid ${mock.primaryM80}`, textAlign: 'center', padding: '6px 0',
}}
            >
              合計
            </div>
          </div>
          {board.staffs.map((staff) => {
            const cell = getCell(staff.staffCode, day.date);
            const live = cell.shifts.filter((s) => !s.deleted);
            const aways = awayShifts(staff.staffCode, day.date);
            const wish = hopeOf(staff.staffCode, day.date);
            const locked = day.locked || (staff.isGuest && live.length === 0 && aways.length === 0);
            const rowVio = !!vioStaff[staff.staffCode];
            const fail = failedOf ? failedOf(staff.staffCode, day.date) : null;
            const minutes = live.filter((s) => s.attendType === 0).reduce((t, s) => t + workOf(s), 0);
            if (live.some((s) => s.attendType === 0)) {
              totalCount += 1;
            }
            totalMinutes += minutes;
            const holiday = live.filter((s) => s.attendType !== 0)[0];
            const own = board.shifts.filter((s) => s.staffCode === staff.staffCode && s.targetDate === day.date && s.orgCode === board.org.orgCode);
            // 未計算のシフトがある行は、人件費・交通費を「未計算」と出す（9/25）
            const rowUncalc = live.some(uncalculated);
            // 交通費はスタッフ × 日 × 勤務組織で 1 回（サーバがセルの最初のシフトにだけ載せる）。ここでも 1 回だけ数える。
            // 人件費は下の段・行末と同じく交通費込み（10/5）
            const dayTrans = isSimulating ? null : own.reduce<number | null>((t, s) => (t !== null ? t : s.transportation), null);
            const dayLabor = isSimulating ? null : own.reduce<number | null>((t, s) => (s.laborCost === null ? t : (t || 0) + s.laborCost), null);
            const dayCost = dayLabor === null ? null : dayLabor + (dayTrans || 0);
            return (
              <div key={staff.staffCode} css={styles.row}>
                <div css={styles.nameCell} style={{ background: rowVio ? mock.errorBg : staff.isGuest ? mock.guestBg : '#fff' }} onClick={() => onStaffTap(staff)}>
                  <StaffAvatar staff={staff} src={photoOf(staff)} hasViolation={!!rowVio} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{
 fontSize: '12px', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
}}
                    >
                      {staff.staffName}
                    </div>
                    <div style={{ fontSize: '10px', color: mock.textSub }}>
                      {holiday ? (staff.distinctionHoliday && holiday.attendType === 1 ? '公休（法定）' : '公休') : staff.executiveName || ''}
                      {cell.changed ? ' ●' : ''}
                    </div>
                  </div>
                  {fail && (
                    <div
                      style={{
 marginLeft: 'auto', flex: 'none', width: '16px', height: '16px', borderRadius: '9999px', background: mock.error, color: '#fff', fontSize: '11px', fontWeight: 700, lineHeight: '16px', textAlign: 'center',
}}
                      title={`保存できませんでした: ${fail}\n保存前の内容に戻しています。もう一度編集してください`}
                    >
                      !
                    </div>
                  )}
                </div>
                <div
                  css={styles.lane}
                  ref={(el) => { laneRef.current[staff.staffCode] = el; }}
                  style={{
                    background: locked || (wish && wish.isOff) ? mock.gray10 : `repeating-linear-gradient(90deg, ${mock.gray10} 0, ${mock.gray10} 1px, transparent 1px, transparent calc(100% / ${hourLabels.length}))`,
                    cursor: locked || !editable ? 'default' : 'pointer',
                  }}
                  onClick={(e) => {
                    if (squelch.current || locked || !editable || drag) {
                      return;
                    }
                    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                    const pct = ((e.clientX - rect.left) / rect.width) * 100;
                    onLaneTap(staff, Math.round((axis.startMinutes + (pct / 100) * axis.totalMinutes) / axis.slotMinutes) * axis.slotMinutes);
                  }}
                >
                  {/*
                    希望（9/24）
                    - シフトが入っている行: バーの上に細い帯で希望の時間帯を出す。バーと重ならないので上下で比べられる
                    - シフトが入っていない行: バーの位置に点線の枠で出す
                    - 休み希望: 行を灰色にし、帯に「休み希望」と出す
                  */}
                  {wish && wish.isOff && (
                    <div
                      css={styles.wishStrip}
                      style={{
 left: '2px', right: '2px', background: mock.gray20, color: mock.gray60,
}}
                    >
                      休み希望
                    </div>
                  )}
                  {wish && !wish.isOff && (() => {
                    const g = wish.start && wish.end ? geo(...spanOf(wish.start, wish.end, false)) : { left: 0.2, width: 99.6 };
                    if (live.length > 0) {
                      return (
                        <div
                          css={styles.wishStrip}
                          style={{
 left: `${g.left}%`, width: `${g.width}%`, background: mock.primaryM80, color: mock.primary,
}}
                          title={`希望 ${wish.label}`}
                        >
                          {`希望 ${wish.label}`}
                        </div>
                      );
                    }
                    return (
                      <div
                        css={styles.bar}
                        style={{
                          left: `${g.left}%`,
                          width: `${g.width}%`,
                          background: '#fff',
                          color: mock.primary,
                          border: `1px dashed ${mock.primary}`,
                          zIndex: 0,
                          pointerEvents: 'none',
                        }}
                      >
                        {`希望 ${wish.label}`}
                      </div>
                    );
                  })()}
                  {live.map((s, i) => {
                    if (s.attendType !== 0 || !s.start || !s.end) {
                      return null;
                    }
                    const dragging = drag && drag.staffCode === staff.staffCode && drag.index === i;
                    const [st, ed] = spanOf(s.start, s.end, s.isEndNextDay);
                    const g = dragging && drag ? geo(drag.curStart, drag.curEnd) : geo(st, ed);
                    const c = colorOf(s.shiftPatternId);
                    // 時刻を先頭に置き、狭いときは末尾（パターン名・実働）から … で切る（9/30 iPad 対応）
                    const label = dragging && drag ? `${minutesToHm(drag.curStart)}–${minutesToHm(drag.curEnd)}` : `${s.start}–${s.end}${s.isEndNextDay ? '翌' : ''}${s.shiftPatternShortName ? ` ${s.shiftPatternShortName}` : ''}（実働 ${formatTime(workOf(s))}）`;
                    return (
                      <div
                        key={i}
                        css={styles.bar}
                        style={{
                          left: `${g.left}%`,
width: `${g.width}%`,
background: staff.isGuest ? `${mock.hatch}, ${c.bg}` : c.bg,
color: c.fg,
                          border: staff.isGuest ? '1px solid rgba(0,0,0,.28)' : (!s.shiftId || cell.changed ? `1px dashed ${mock.primary}` : '1px solid transparent'),
                          cursor: locked || !editable ? 'default' : 'grab',
opacity: dragging ? 0.8 : 1,
zIndex: dragging ? 3 : 1,
                        }}
                        title={label}
                        onPointerDown={(e) => beginDrag(staff, i, s, e)}
                        onPointerMove={onDragMove}
                        onPointerUp={() => onDragEnd(staff, i, s)}
                        onPointerCancel={() => setDrag(null)}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {!locked && editable && <div css={styles.handle} style={{ left: 0 }} />}
                        {label}
                        {!locked && editable && <div css={styles.handle} style={{ right: 0 }} />}
                      </div>
                    );
                  })}
                  {aways.map((a) => {
                    if (a.attendType !== 0 || !a.start || !a.end) {
                      return null;
                    }
                    const [st, ed] = spanOf(a.start, a.end, a.isEndNextDay);
                    const g = geo(st, ed);
                    return (
                      <div
                        key={a.shiftId}
                        css={styles.bar}
                        style={{
 left: `${g.left}%`, width: `${g.width}%`, background: '#fff', color: mock.gray60, border: `1px dashed ${mock.gray40}`,
}}
                      >
                        <span title={`${a.awayOrgName || '支援'} ${a.start}–${a.end}`}>{`→${a.awayOrgName || '支援'} ${a.start}–${a.end}`}</span>
                      </div>
                    );
                  })}
                  {rowVio && live.length > 0 && (
                  <div style={{
 position: 'absolute', inset: '6px 0', border: `2px solid ${mock.error}`, borderRadius: '3px', pointerEvents: 'none',
}}
                  />
)}
                </div>
                <div css={styles.total}>
                  <div css={styles.totalLine} style={{ justifyContent: 'flex-end' }}>
                    <span style={{ fontSize: '13px', fontWeight: 700 }} title={rowUncalc ? '確定して公開すると正しく計算されます（今は開始・終了・休憩からの目安）' : undefined}>{minutes > 0 ? `${hours(minutes)}` : '—'}</span>
                  </div>
                  {showLaborCost && minutes > 0 && (
                    <div css={styles.totalLine}>
                      <span style={{ fontSize: '9px', color: mock.textSub }} title={LABOR_COST_TIP}>人件費</span>
                      <span style={{ fontSize: rowUncalc ? '10px' : '11px', fontWeight: 700, color: rowUncalc ? mock.textSub : undefined }}>{rowUncalc ? NOT_YET : yen(dayCost)}</span>
                    </div>
                  )}
                  {showLaborCost && minutes > 0 && (
                    <div css={styles.totalLine}>
                      <span style={{ fontSize: '9px', color: mock.textSub }} title={TRANSPORTATION_TIP}>交通費</span>
                      <span style={{ fontSize: rowUncalc ? '10px' : '11px', color: rowUncalc ? mock.textSub : undefined }}>{rowUncalc ? NOT_YET : yen(dayTrans)}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          <div css={styles.foot}>
            <div style={{
 width: `${NAME_W}px`, flex: 'none', padding: '6px 8px', fontSize: '11px', fontWeight: 700, background: mock.primaryM95, ...STICKY_NAME,
}}
            >
              {`日計（${totalCount}名）`}
            </div>
            <div style={{ flex: 1 }} />
            <div css={styles.total} style={{ padding: '4px 6px' }}>
              <div css={styles.totalLine} style={{ justifyContent: 'flex-end' }}>
                <span style={{ fontSize: '13px', fontWeight: 700 }}>{`${hours(totalMinutes)}`}</span>
              </div>
              {showLaborCost && (
                <div css={styles.totalLine}>
                  <span style={{ fontSize: '9px', color: mock.textSub }} title={LABOR_COST_TIP}>人件費</span>
                  <span style={{ fontSize: '11px', fontWeight: 700, color: dayUncalc ? mock.textSub : undefined }}>{dayUncalc ? NOT_YET : dayTotal ? yen(dayTotal.laborCost) : '—'}</span>
                </div>
              )}
              {showLaborCost && (
                <div css={styles.totalLine}>
                  <span style={{ fontSize: '9px', color: mock.textSub }} title={TRANSPORTATION_TIP}>交通費</span>
                  <span style={{ fontSize: '11px', color: dayUncalc ? mock.textSub : undefined }}>{dayUncalc ? NOT_YET : dayTotal ? yen(dayTotal.transportation) : '—'}</span>
                </div>
              )}
            </div>
          </div>
          <div style={{ fontSize: '10px', color: mock.textSub, padding: '6px 8px' }}>
            {isSimulating ? '保存中… 変更はその場で保存されます（人件費は確定したときに再計算）' : 'バーをドラッグで移動、端をドラッグで時間調整。クリックで編集。変更はその場で保存されます'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DayTimeline;
